import React from "react";
import ReactMarkdown from "react-markdown";
import "./EnvelopeContent.css";

/* ----------------------------------------------------------------------------
 * Envelope rendering helpers (markdown first, then cards)
 * ------------------------------------------------------------------------- */

function domainFromHref(href = "") {
  try {
    return new URL(href).hostname.replace(/^www\./, "");
  } catch {
    return "";
  }
}

export function isChatEnvelope(env) {
  return (
    env && typeof env === "object" && env.version === "uce/1" && env.render
  );
}

function normalizeCard(c) {
  const href = c.url || c.contextLink || c.link || "#";
  const img = c.thumbnail || c.image_url || c.imageUrl || c.image || null;
  const title = c.title || c.name || c.ref || "Untitled";
  const subtitle =
    c.subtitle || c.site || domainFromHref(c.contextLink || c.link || c.url);
  let price = null;
  if (
    c.price &&
    typeof c.price === "object" &&
    typeof c.price.amount === "number"
  ) {
    price = `${c.price.amount.toFixed(2)} ${c.price.currency || ""}`.trim();
  }
  return { href, img, title, subtitle, price, raw: c };
}

function CardsGrid({ cards, onVisualize, onVisit }) {
  if (!cards?.length) return null;

  const handleVisit = (card, e) => {
    e?.stopPropagation(); // don't trigger the card-level click
    onVisit?.(card);
    const href =
      card?.url || card?.contextLink || card?.link || card?.href || null;
    if (href) window.open(href, "_blank", "noopener,noreferrer");
  };

  const handleVisualizeClick = (card) => {
    console.log("handleVisualizeClick", card);
    // Entire card click -> visualize handler (copy to chat input)
    onVisualize?.(card);
  };

  const handleCardKey = (card, e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onVisualize?.(card);
    }
  };

  return (
    <div className="chat-cards-grid">
      {cards.map((raw, i) => {
        const c = normalizeCard(raw);
        return (
          <div
            key={i}
            className="chat-card"
            title={c.title}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => handleCardKey(c.raw, e)}
          >
            <div className="chat-card-thumb">
              {c.img ? (
                <img src={c.img} alt={c.title} />
              ) : (
                <div className="chat-card-thumb--empty" />
              )}
            </div>

            <div className="chat-card-body">
              <div className="chat-card-title">{c.title}</div>
              <div className="chat-card-meta">
                {c.subtitle && (
                  <span className="chat-card-sub">{c.subtitle}</span>
                )}
                {c.price && <span className="chat-card-price">{c.price}</span>}
              </div>
            </div>

            <div className="chat-card-actions">
              <button
                className="lh-btn primary"
                type="button"
                onClick={(e) => handleVisualizeClick(c.raw, e)}
              >
                Visualize in room
              </button>
              <button
                className="lh-btn"
                type="button"
                onClick={(e) => handleVisit(c.raw, e)}
              >
                View on site
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/** EnvelopeContent
 * Renders:
 *  1) render.markdown (as markdown)
 *  2) render.cards (as a grid of interactive cards)
 *
 * Props:
 * - envelope: ChatEnvelope
 * - onVisualize(card): called on whole-card click or "Visualize in room"
 * - onVisit(card): called on "View on site" button (opens new tab)
 */
export default function EnvelopeContent({ envelope, onVisualize, onVisit }) {
  if (!isChatEnvelope(envelope)) {
    return (
      <pre
        style={{ margin: 0, fontSize: 12.5, lineHeight: 1.4, overflow: "auto" }}
      >
        {JSON.stringify(envelope, null, 2)}
      </pre>
    );
  }

  const md = envelope?.render?.markdown || "";
  const cards = Array.isArray(envelope?.render?.cards)
    ? envelope.render.cards
    : [];

  return (
    <div className="envelope-content">
      {md ? (
        <div className="envelope-markdown">
          <ReactMarkdown>{md}</ReactMarkdown>
        </div>
      ) : null}

      {cards.length ? (
        <div className="envelope-cards">
          <CardsGrid
            cards={cards}
            onVisualize={onVisualize}
            onVisit={onVisit}
          />
        </div>
      ) : null}
    </div>
  );
}
